import { Paginator } from "primereact/paginator";
import { classNames } from "primereact/utils";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { PushDeliveryStatus } from "../../../../common/enums";
import { CommunicationLogItem } from "../components/CommunicationLogItem";
import { CommunicationLogDialogMeta } from "../components/CommunicationLogDialogMeta";
import { PhoneScreen } from "../components/PhoneScreen";
import { usePushLogsList } from "./hooks/usePushLogsList";
import styles from "./PushLogsList.module.scss";

// Same palette as the status chips, as text only.
const DEVICE_STATUS_CLASS: Record<PushDeliveryStatus, string> = {
  [PushDeliveryStatus.Sent]: styles.dialog_device_status_sent,
  [PushDeliveryStatus.Delivered]: styles.dialog_device_status_delivered,
  [PushDeliveryStatus.Opened]: styles.dialog_device_status_delivered,
  [PushDeliveryStatus.Failed]: styles.dialog_device_status_failed,
};

interface IPushLogsListProps {
  filterUserIds: number[] | null;
}

export const PushLogsList = ({ filterUserIds }: IPushLogsListProps) => {
  const {
    page,
    items,
    totalCount,
    isLoading,
    selectedPush,
    setSelectedPush,
    handlePageChange,
    PAGE_SIZE,
  } = usePushLogsList({ filterUserIds });

  return (
    <div className={styles.root}>
      <div className={styles.root_list}>
        {isLoading &&
          Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className={styles.root_list_skeleton} />
          ))}

        {!isLoading && items.length === 0 && (
          <div className={styles.root_empty}>Няма намерени известия.</div>
        )}

        {!isLoading &&
          items.map((item) => (
            <CommunicationLogItem
              key={item.id}
              index={item.index}
              receiverName={item.receiverName}
              receiver={item.typeLabel}
              details={item.body}
              status={item.status}
              createdOn={item.createdOn}
              onClick={() => setSelectedPush(item)}
            />
          ))}
      </div>

      {totalCount > PAGE_SIZE && (
        <Paginator
          first={(page - 1) * PAGE_SIZE}
          rows={PAGE_SIZE}
          totalRecords={totalCount}
          onPageChange={handlePageChange}
          className={styles.root_paginator}
        />
      )}

      <AppDialog
        visible={!!selectedPush}
        onHide={() => setSelectedPush(null)}
        header="Известие"
        style={{ width: "90vw", maxWidth: "500px" }}
      >
        <div className={styles.dialog_content}>
          <CommunicationLogDialogMeta
            receiver={
              selectedPush?.receiverName
                ? `${selectedPush.receiverName} • ${selectedPush.typeLabel}`
                : selectedPush?.typeLabel
            }
            status={selectedPush?.status}
            errorMessage={selectedPush?.errorMessage}
          />

          <PhoneScreen className={styles.dialog_lock_screen}>
            <div className={styles.dialog_lock_time}>{selectedPush?.lockScreenTime}</div>
            <div className={styles.dialog_lock_date}>{selectedPush?.lockScreenDate}</div>

            <div className={styles.dialog_notification_placeholder}>
              <div className={styles.dialog_placeholder_line} style={{ width: "45%" }} />
              <div className={styles.dialog_placeholder_line} style={{ width: "85%" }} />
            </div>

            <div className={styles.dialog_notification}>
              <div className={styles.dialog_notification_header}>
                <img src="/icon-192.png" alt="" className={styles.dialog_notification_icon} />
                <span className={styles.dialog_notification_app}>Planny</span>
                <span className={styles.dialog_notification_time}>
                  {selectedPush?.lockScreenTime}
                </span>
              </div>
              <div className={styles.dialog_notification_title}>{selectedPush?.title}</div>
              <div className={styles.dialog_notification_body}>{selectedPush?.body}</div>
            </div>
          </PhoneScreen>

          <div className={styles.dialog_devices}>
            <span className={styles.dialog_devices_title}>Устройства</span>

            {selectedPush?.devices.length === 0 && (
              <span className={styles.dialog_devices_empty}>
                Няма записани устройства за това известие.
              </span>
            )}

            {selectedPush?.devices.map((device) => (
              <div key={device.key} className={styles.dialog_device}>
                <div className={styles.dialog_device_main}>
                  <span className={styles.dialog_device_name}>{device.deviceLabel}</span>
                  <span
                    className={classNames(
                      styles.dialog_device_status,
                      DEVICE_STATUS_CLASS[device.status],
                    )}
                  >
                    {device.statusLabel}
                  </span>
                </div>
                {(device.deliveredOn || device.openedOn) && (
                  <div className={styles.dialog_device_times}>
                    {device.deliveredOn && <span>Получено {device.deliveredOn}</span>}
                    {device.openedOn && <span>Отворено {device.openedOn}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </AppDialog>
    </div>
  );
};
